import type { QuestionEvaluationRow } from '@compass/client/types/analytics';
import React, { useMemo } from 'react';

import { GenericTable } from '@/components/GenericTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SupportedTimeZone } from '@/constants/date';
import { deriveAnswer, formatPercent1 } from '@/pages/analytics/utils/questionDeepDive';
import type { TableColumnConfig, TableConfig } from '@/types/table';
import { cn } from '@/utils';
// formatDateTimeCompact maps the app's SupportedTimeZone (e.g. "ET") to an IANA
// zone via TIMEZONE_MAP — a raw Intl.DateTimeFormat({ timeZone: "ET" }) throws.
import { formatDateTimeCompact } from '@/utils/date';
import { buildOrgRoutePath } from '@/utils/routes';

import { ChartCard } from './ChartCard';
import type { ChartDownloadFormat } from './ChartDownloadMenu';

// The list is a preview: at most this many rows, then "View all evaluations".
const MAX_ROWS = 5;

interface QuestionEvaluationsTableProps {
  organizationId: string;
  rows: QuestionEvaluationRow[];
  loading?: boolean;
  timeZone: SupportedTimeZone;
  // Total missed count in scope (from the summary), shown in the subtitle. The
  // table itself only previews the 5 most recent; the full set is available via
  // the download menu.
  totalMissed?: number;
  // Downloads the FULL missed-evaluation list (not just the 5 previewed).
  onDownload?: (format: ChartDownloadFormat) => void | Promise<void>;
  // Set when the evaluations request failed. Surfaced explicitly so a failed
  // load isn't mistaken for "no missed evaluations" (they look identical when
  // the error is swallowed — the summary can still show a non-zero miss count).
  error?: boolean;
  onRetry?: () => void;
}

// Eval ID is a 24-char ObjectId shown in a mono font — needs room so it isn't
// truncated to "6a88…".
const EVAL_COLUMN_WIDTH = 230;
// Agent is usually an email (~30 chars) — needs room so it isn't truncated to
// "anything@ydp1izk5.mailosaur…".
const AGENT_COLUMN_WIDTH = 420;
const DATE_COLUMN_WIDTH = 280;

export const QuestionEvaluationsTable: React.FC<QuestionEvaluationsTableProps> = ({
  organizationId,
  rows,
  loading,
  timeZone,
  totalMissed,
  onDownload,
  error,
  onRetry,
}) => {
  const previewRows = useMemo(() => rows.slice(0, MAX_ROWS), [rows]);

  const columns: TableColumnConfig<QuestionEvaluationRow>[] = useMemo(
    () => [
      {
        key: 'evaluation_id',
        header: 'Eval ID',
        width: EVAL_COLUMN_WIDTH,
        accessor: item => (
          <span className="block truncate font-mono text-xs text-muted-foreground">
            {item.evaluation_id}
          </span>
        ),
      },
      {
        key: 'agent_name',
        header: 'Agent',
        width: AGENT_COLUMN_WIDTH,
        accessor: item => (
          <span className="block truncate text-foreground">
            {item.agent_name || item.agent_id || '—'}
          </span>
        ),
      },
      {
        key: 'call_start_time',
        header: 'Call Start Time',
        width: DATE_COLUMN_WIDTH,
        accessor: item => (
          <span className="tabular-nums text-muted-foreground">
            {formatDateTimeCompact(item.call_start_time, timeZone)}
          </span>
        ),
      },
      {
        key: 'aggregate_score',
        header: 'Score',
        accessor: item => (
          <span className="tabular-nums">{formatPercent1(item.aggregate_score ?? 0)}</span>
        ),
      },
      {
        key: 'answer',
        header: 'Answer',
        accessor: item => {
          const answer = deriveAnswer(item.raw_score ?? 0);
          return (
            <Badge
              variant="outline"
              className={cn(
                'border-transparent',
                answer === 'No'
                  ? 'bg-critical text-critical-foreground'
                  : 'bg-attention text-attention-foreground',
              )}
            >
              {answer}
            </Badge>
          );
        },
      },
    ],
    [timeZone],
  );

  const config: TableConfig<QuestionEvaluationRow> = useMemo(
    () => ({
      columns,
      // Row opens the existing evaluation detail page, which carries the full
      // per-question evidence (kept out of ClickHouse per the PHI boundary).
      getRowLink: item => buildOrgRoutePath(`/qc/${item.evaluation_id}`, organizationId),
      getRowKey: item => item.evaluation_id ?? '',
      emptyState: {
        title: 'No missed evaluations',
        description: 'No evaluation missed this question for the current filters.',
      },
    }),
    [columns, organizationId],
  );

  return (
    <ChartCard
      title="Evaluations With Lost Points"
      info="Every evaluation where this question was missed, with the recorded answer and evidence."
      onDownload={onDownload}
      downloadDisabled={!!error || previewRows.length === 0}
      bodyClassName="px-4 pb-4 pt-0 space-y-2"
    >
      <p className="text-xs text-muted-foreground">
        {typeof totalMissed === 'number' && totalMissed > previewRows.length
          ? `Showing the ${previewRows.length} most recent of ${totalMissed} — download for the full list.`
          : `Every evaluation where this question was missed${
              typeof totalMissed === 'number' ? ` — ${totalMissed} in scope` : ''
            }.`}
      </p>
      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <p className="text-destructive">Couldn&apos;t load the evaluations for this question.</p>
          {onRetry ? (
            <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </div>
      ) : (
        <GenericTable<QuestionEvaluationRow>
          config={config}
          data={previewRows}
          loading={loading}
          error={null}
          currentPage={1}
          hasNextPage={false}
          onPageChange={() => {}}
          hidePagination
        />
      )}
    </ChartCard>
  );
};
